import { useNavigate } from "react-router-dom";
import { faCalculator, faPencil, faPlus } from "@fortawesome/free-solid-svg-icons";
import { Grid, useTheme } from "@mui/material";
import { DataResult, State } from "@progress/kendo-data-query";
import { useRef, useState } from "react";
import {
  GridDataStateChangeEvent,
  GridNoRecords,
  GridRowDoubleClickEvent,
  GridToolbar,
} from "@progress/kendo-react-grid";
import { GridColumn } from "@progress/kendo-react-grid/dist/npm/GridColumn";
import axios, { AxiosError } from "axios";
import { trackPromise } from "react-promise-tracker";
import { spdrUrls } from "../../../URL/ApplicationUrls";
import PageHeader from "../../../Layout/Header/PageHeader";
import { SpdrOverviewItem } from "./SpdrOverviewItem";
import { SpdrOverviewColumns } from "./SpdrOverviewColumns";
import { combinedUrls } from "../../../URL/functions";
import { useSpdrOverviewActions } from "./useSpdrOverviewActions";
import { spdrOverviewToolbarMenu } from "./SpdrOverviewToolbarMenu";
import useSavedState from "../../../Grid/UseSavedState";
import { InitialState } from "../../../Grid/InitialDataState";
import { OnRowClickType } from "../../../Layout/Grid/GridRowClickProps";
import { GridSelectableRowsProps } from "../../../Grid/GridSelectableRowsProps";
import StyledKendoGrid from "../../../Grid/StyledKendoGrid";
import { GridDataLoader } from "../../../Grid/GridDataLoader";
import { KENDO_BUTTON_CLASSES } from "../../../Layout/Buttons/KendoButtons";
import { IconWithRightMargin } from "../../../Layout/Icons/IconWithRightMargin";
import { UppercaseButton } from "../../../Layout/Buttons/UppercaseButton";
import MdmQuickFilter from "../../../Layout/Filter/MdmQuickFilter";
import { GRID_SELECTED_FIELD } from "../../../Grid/Selectable/GridSelection";
import MdmRelationQuickFilter from "../../Filter/MdmRelationQuickFilter";
import { SPDR_REGEX, SPR_REGEX } from "../../Filter/RelationRegExp";
import { lastUpdatedTimeStamp } from "../../lastUpdatedTimeStamp";
import StatePersistenceButtons from "../../../Grid/StatePersistenceButtons";
import { isColumnFilterActive } from "../../../Grid/Extensions/FilterStatusHelper";
import { DefaultOverviewFilter } from "../../Filter/DefaultOverviewFilter";
import { useOverviewLastUpdated } from "../../../Axios/useOverviewLastUpdated";
import {
  showNotSelfClosingActionFailureToast,
  showNotSelfClosingActionStartToast,
  showNotSelfClosingActionSuccessToast,
  ToastId,
} from "../../NotificationForActionButton";
import { DATA_ITEM_KEY, gridPagerDefaultSettings } from "../../../Grid/GridConstants";
import { getSpdrIdOriginTable } from "../../../Grid/gridUtils";
import HttpStatusCode from "../../../Utils/HttpConstants";
import GridContentSpinner from "../../../Grid/GridContentSpinner";
import fetchGridData from "../../../Grid/Functions/fetchGridData";
import { emptyDataGridResult } from "../../../Grid/Functions/emptyDataGridResult";
import RenderAuthorized from "../../../Auth/RenderAuthorized";
import { AuthorizationPolicies } from "../../../Auth/AuthorizationPolicy";
import { MdmApiError, mdmApiErrorMessageOrFallbackMessage } from "../../../Errors/MdmApiError";

const SPDROVERVIEWGRID_DATASTATE = "SpdrOverviewGrid_DataState";

export function SpdrOverview() {
  const navigate = useNavigate();
  const [dataResult, setDataResult] = useState<DataResult>(emptyDataGridResult);

  const initialDataState: State = {
    ...InitialState,
    filter: DefaultOverviewFilter,
  };

  const [dataState, setDataState] = useSavedState<State>(
    SPDROVERVIEWGRID_DATASTATE,
    initialDataState
  );
  const [selectedState, setSelectedState] = useState<{
    [id: number]: boolean | number[];
  }>({});
  const [selectedIds, setSelectedIds] = useState<Array<number>>([]);
  const [allItemsSelected, setAllItemsSelected] = useState<boolean>(false);
  const selectableRowsProps = GridSelectableRowsProps<SpdrOverviewItem>({
    selectedState,
    setSelectedState,
    setSelectedIds,
    dataItemKey: DATA_ITEM_KEY,
    setAllItemsSelected,
  });

  const toastId: ToastId = useRef(null);

  const dataStateChange = (e: GridDataStateChangeEvent) => {
    setDataState(e.dataState);
  };

  const handleRowDoubleClick = (e: GridRowDoubleClickEvent) => {
    const spdr = e.dataItem as SpdrOverviewItem;
    navigate(spdrUrls.spdrWithId(spdr.SpdrIdOriginTable).uiPath);
  };

  const onDataReceived = (spdrs: DataResult) => {
    setDataResult(spdrs);
  };

  const onRowClick: OnRowClickType = (dataItem) => {
    const id = dataItem.Id;
    setSelectedState({ [id]: true });
    setSelectedIds([...selectedIds, id]);
  };

  const addSpdr = () => navigate(spdrUrls.addSpdr.uiPath);

  const editSpdr = (spdr: number | undefined) => spdr && navigate(spdrUrls.spdrWithId(spdr).uiPath);

  const editButton = {
    onClick: (selectedSpdrs: number[]) => selectedSpdrs.length === 1 && editSpdr(selectedSpdrs[0]),
    text: "EDIT",
    icon: faPencil,
    disabled: (selectedItems: SpdrOverviewItem[]) => selectedItems.length !== 1,
    roles: AuthorizationPolicies.RequireAdminNetworkRoles,
  };

  const {
    expireSpdrsButton,
    excludeSpdrsFromScopeButton,
    createProjectFromSpdrsButton,
    createTtProtectionFromSpdrsButton,
  } = useSpdrOverviewActions();

  const { data: lastUpdated, refetch: refetchLastUpdated } = useOverviewLastUpdated(
    spdrUrls.overviewLastUpdated,
    "spdrs"
  );

  const spdrExportMenu = spdrOverviewToolbarMenu();
  const exportButton = spdrExportMenu(dataState);

  const toolbarItems = [
    editButton,
    expireSpdrsButton,
    excludeSpdrsFromScopeButton,
    createProjectFromSpdrsButton,
    createTtProtectionFromSpdrsButton,
  ];

  const GridRef = useRef<any>();
  const fetchDataUrl = combinedUrls(spdrUrls.base.apiUrl, "KendoGrid");
  const theme = useTheme();
  const [recalculationTriggered, setRecalculationTriggered] = useState<boolean>(false);

  const columns = SpdrOverviewColumns({
    dataResult,
    selectedState,
    onRowClick,
  });

  const triggerCalculation = () => {
    setRecalculationTriggered(true);
    toastId.current = showNotSelfClosingActionStartToast("Calculating SPDRs");
    axios
      .get(spdrUrls.triggerCalculation)
      .then((response) => {
        showNotSelfClosingActionSuccessToast(toastId, "SPDR calculation successful");
        if (response.status === HttpStatusCode.OK) {
          trackPromise(refetchLastUpdated());
          trackPromise<DataResult>(fetchGridData(fetchDataUrl, dataState)).then((newDataResult) =>
            setDataResult(newDataResult)
          );
        }
      })
      .catch((error: AxiosError<MdmApiError>) =>
        showNotSelfClosingActionFailureToast(
          toastId,
          mdmApiErrorMessageOrFallbackMessage(error, "Error while calculating SPDRs")
        )
      )
      .finally(() => setRecalculationTriggered(false));
  };

  return (
    <>
      <PageHeader headerText="SPDR Overview">{lastUpdatedTimeStamp(lastUpdated)}</PageHeader>
      <Grid key="customFilterSpdr" container justifyContent="left" ml={-1} spacing={0.5}>
        <Grid item key="routeNumberFilter">
          <MdmQuickFilter
            currentDataState={dataState}
            setDataState={setDataState}
            findText="Route Number"
            filterFields={[
              "FirstOdcInbound",
              "SecondOdcInbound",
              "ThirdOdcInbound",
              "FourthOdcInbound",
              "FifthOdcInbound",
              "InboundToPlantInbound",
            ]}
          />
        </Grid>
        <Grid item key="odcFilter">
          <MdmQuickFilter
            currentDataState={dataState}
            setDataState={setDataState}
            findText="ODC"
            filterFields={[
              "FirstOdcShortName",
              "SecondOdcShortName",
              "ThirdOdcShortName",
              "FourthOdcShortName",
              "FifthOdcShortName",
            ]}
          />
        </Grid>
        <Grid item key="trainFerryFilter">
          <MdmQuickFilter
            currentDataState={dataState}
            setDataState={setDataState}
            findText="Train/Ferry GSDB"
            filterFields={[
              "FirstOdcTrainFerry",
              "SecondOdcTrainFerry",
              "ThirdOdcTrainFerry",
              "FourthOdcTrainFerry",
              "FifthOdcTrainFerry",
            ]}
          />
        </Grid>
        <Grid item key="sprSpdrFilter">
          <MdmRelationQuickFilter
            currentDataState={dataState}
            findText="SPR / SPDR"
            triggerFiltering={setDataState}
            relationMatchers={[
              {
                relation: "SPR",
                regex: SPR_REGEX,
              },
              {
                relation: "SPDR",
                regex: SPDR_REGEX,
              },
            ]}
          />
        </Grid>
      </Grid>
      <StyledKendoGrid
        muiTheme={theme}
        ref={GridRef}
        height="65vh"
        sortable
        filterable
        pageable={{
          ...gridPagerDefaultSettings,
        }}
        resizable
        scrollable="scrollable"
        {...dataState}
        data={{
          data: dataResult.data.map((item) => ({
            ...item,
            [GRID_SELECTED_FIELD]: selectedState[item.Id],
          })),
          total: dataResult.total,
        }}
        onDataStateChange={dataStateChange}
        onRowDoubleClick={handleRowDoubleClick}
        rowHeight={50}
        {...selectableRowsProps}
      >
        <GridToolbar>
          <StatePersistenceButtons
            localStorageKey={SPDROVERVIEWGRID_DATASTATE}
            initialState={initialDataState}
            dataState={dataState}
            resetButtonText="RESET FILTER"
            saveButtonText="SAVE FILTER"
            align="left"
          />
          <RenderAuthorized requiredRoles={AuthorizationPolicies.RequireCalculationTriggerRole}>
            <UppercaseButton
              type="button"
              title="Recalculate Overview"
              className={KENDO_BUTTON_CLASSES}
              onClick={triggerCalculation}
              disabled={recalculationTriggered}
            >
              <IconWithRightMargin icon={faCalculator} />
              Recalculate Overview
            </UppercaseButton>
          </RenderAuthorized>
          <RenderAuthorized requiredRoles={AuthorizationPolicies.RequireAdminNetworkRoles}>
            <UppercaseButton
              type="button"
              title="ADD"
              className={KENDO_BUTTON_CLASSES}
              onClick={addSpdr}
            >
              <IconWithRightMargin icon={faPlus} />
              ADD
            </UppercaseButton>
          </RenderAuthorized>
          {toolbarItems.map((item) => (
            <RenderAuthorized requiredRoles={item.roles} key={item.text}>
              <UppercaseButton
                key={item.text}
                type="button"
                title={item.text}
                className={KENDO_BUTTON_CLASSES}
                onClick={async () => {
                  const spdrIds: number[] = await getSpdrIdOriginTable({
                    selectedIds,
                    currentDatastate: dataState,
                    tableDataResult: dataResult,
                    allItemsSelected,
                  });
                  item.onClick(spdrIds);
                }}
                disabled={selectedIds.length === 0}
              >
                <IconWithRightMargin icon={item.icon} />
                {item.text}
              </UppercaseButton>
            </RenderAuthorized>
          ))}
          {exportButton}
        </GridToolbar>
        <GridNoRecords>There is no data available</GridNoRecords>

        {columns.map((column) => (
          <GridColumn
            key={column.key}
            {...column.props}
            headerClassName={
              isColumnFilterActive(column.props.field, dataState) ? "filterActive" : ""
            }
          />
        ))}
      </StyledKendoGrid>
      <GridContentSpinner />
      <GridDataLoader url={fetchDataUrl} dataState={dataState} onDataReceived={onDataReceived} />
    </>
  );
}
